import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Plus } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { Distribuidora, RateioUnidadeGeradora } from '../../../types';
import DistribuidoraFormModal from './DistribuidoraFormModal';
import { capacidadeDivergente, kwpDaTelemetria, limparNumeroUsina, normUc, validarNumerosUsina } from '../../../src/lib/vinculoPlanilhaRateio';

interface UGFormModalProps {
  ug?: RateioUnidadeGeradora | null;
  onClose: () => void;
  onSaved: (ug: RateioUnidadeGeradora) => void;
}

const UGFormModal: React.FC<UGFormModalProps> = ({ ug, onClose, onSaved }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [nome, setNome] = useState(ug?.nome || '');
  const [distribuidoraId, setDistribuidoraId] = useState(ug?.distribuidora_id || '');
  const [capacidadeKwp, setCapacidadeKwp] = useState(ug?.capacidade_kwp != null ? String(ug.capacidade_kwp) : '');
  const [ativo, setAtivo] = useState(ug?.ativo ?? true);
  // Número longo = o que aparece nas faturas (e que a extração usa para achar as
  // usinas Vigor). Número antigo = o que a planilha de rateio usa (#5838).
  const [numeroLongo, setNumeroLongo] = useState(ug?.numero_uc_geradora ?? '');
  const [numeroAntigo, setNumeroAntigo] = useState(ug?.numero_uc_geradora_antigo ?? '');
  // Capacidade que a telemetria informa (em MW lá, kWp aqui), para conferir o
  // cadastro: as Vera Cruz estavam com 1.000 kWp e o inversor diz 1.360, e uma
  // Cooperativa Centro-Sul estava com 500.000 (#5838).
  const [kwpTelemetria, setKwpTelemetria] = useState<number | null>(null);
  const [nomeTelemetria, setNomeTelemetria] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [distribuidoras, setDistribuidoras] = useState<Distribuidora[]>([]);
  const [loadingDistribuidoras, setLoadingDistribuidoras] = useState(true);
  const [showNovaDistribuidora, setShowNovaDistribuidora] = useState(false);
  const fecharRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { fecharRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving && !showNovaDistribuidora) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, showNovaDistribuidora, onClose]);

  const carregarDistribuidoras = async () => {
    setLoadingDistribuidoras(true);
    try {
      const { data, error } = await supabase.from('distribuidora_config').select('*').eq('ativo', true).order('nome');
      if (error) throw error;
      setDistribuidoras((data || []) as Distribuidora[]);
    } catch (error) {
      console.error('Error loading distribuidoras', error);
    } finally {
      setLoadingDistribuidoras(false);
    }
  };

  useEffect(() => {
    carregarDistribuidoras();
  }, []);

  useEffect(() => {
    if (!ug?.telemetria_site_id) return;
    let vivo = true;
    (async () => {
      const { data, error } = await supabase
        .from('unidades_geradoras')
        .select('nome_usina, capacidade_instalada')
        .eq('site_id_api', ug.telemetria_site_id)
        .maybeSingle();
      if (!vivo || error || !data) return;
      setKwpTelemetria(kwpDaTelemetria((data as { capacidade_instalada: number | null }).capacidade_instalada));
      setNomeTelemetria((data as { nome_usina: string | null }).nome_usina ?? null);
    })();
    return () => { vivo = false; };
  }, [ug?.telemetria_site_id]);

  const capacidadeAtual = capacidadeKwp.trim() ? parseFloat(capacidadeKwp.replace(',', '.')) : null;
  const divergente = capacidadeDivergente(Number.isFinite(capacidadeAtual as number) ? capacidadeAtual : null, kwpTelemetria);

  const handleSave = async () => {
    if (!nome.trim()) {
      addToast('Informe o nome da UG.', 'error');
      return;
    }
    const capacidade = capacidadeKwp.trim() ? parseFloat(capacidadeKwp.replace(',', '.')) : null;
    if (capacidade != null && (!Number.isFinite(capacidade) || capacidade < 0)) {
      addToast('Capacidade instalada inválida.', 'error');
      return;
    }

    const longo = limparNumeroUsina(numeroLongo);
    const antigo = limparNumeroUsina(numeroAntigo);
    // Valida só o número que mudou: um conflito antigo no cadastro não pode
    // travar a edição dos outros campos da usina.
    const longoMudou = normUc(longo) !== normUc(ug?.numero_uc_geradora);
    const antigoMudou = normUc(antigo) !== normUc(ug?.numero_uc_geradora_antigo);
    if (longoMudou || antigoMudou) {
      const { data: outras, error: erroOutras } = await supabase
        .from('rateio_unidades_geradoras')
        .select('id, nome, numero_uc_geradora, numero_uc_geradora_antigo');
      if (erroOutras) {
        addToast('Não foi possível conferir os números das outras usinas. Tente de novo.', 'error');
        return;
      }
      const erroNumero = validarNumerosUsina(longoMudou ? longo : null, antigoMudou ? antigo : null, outras ?? [], ug?.id ?? null)
        ?? (longoMudou && antigoMudou ? validarNumerosUsina(longo, antigo, [], null) : null);
      if (erroNumero) {
        addToast(erroNumero, 'error');
        return;
      }
    }
    const numeros = { numero_uc_geradora: longo, numero_uc_geradora_antigo: antigo };

    setSaving(true);
    try {
      if (ug) {
        const { data, error } = await supabase
          .from('rateio_unidades_geradoras')
          .update({ nome: nome.trim(), distribuidora_id: distribuidoraId || null, capacidade_kwp: capacidade, ativo, ...numeros })
          .eq('id', ug.id)
          .select('*, distribuidora_config(nome, email_protocolo, dia_leitura_padrao)')
          .single();
        if (error) throw error;
        addToast('UG atualizada com sucesso.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'atualizar_ug', origem: 'acao' });
        onSaved(data as RateioUnidadeGeradora);
      } else {
        const { data, error } = await supabase
          .from('rateio_unidades_geradoras')
          .insert({
            nome: nome.trim(),
            distribuidora_id: distribuidoraId || null,
            capacidade_kwp: capacidade,
            ativo,
            ...numeros,
            created_by: profile?.id,
          })
          .select('*, distribuidora_config(nome, email_protocolo, dia_leitura_padrao)')
          .single();
        if (error) throw error;
        addToast('UG criada — fica pendente de aprovação de um Gestor.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_simulador', acao: 'criar_ug', origem: 'acao' });
        onSaved(data as RateioUnidadeGeradora);
      }
    } catch (error) {
      console.error('Error saving UG', error);
      addToast('Erro ao salvar a UG.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="presentation">
      <div role="dialog" aria-modal="true" aria-labelledby="ug-form-titulo" className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 id="ug-form-titulo" className="text-lg font-bold text-gray-900 dark:text-white font-poppins">
            {ug ? 'Editar UG' : 'Nova UG'}
          </h2>
          <button ref={fecharRef} onClick={onClose} disabled={saving} aria-label="Fechar" className="text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded p-1">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome *</label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Usina Solar Vigor I"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Distribuidora</label>
            <select
              value={distribuidoraId}
              onChange={e => setDistribuidoraId(e.target.value)}
              disabled={loadingDistribuidoras}
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 disabled:opacity-60"
            >
              <option value="">Selecione...</option>
              {distribuidoras.map(d => (
                <option key={d.id} value={d.id}>
                  {d.nome}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowNovaDistribuidora(true)}
              className="mt-1.5 text-xs text-primary hover:underline flex items-center gap-1"
            >
              <Plus size={12} /> Cadastrar nova distribuidora
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Capacidade Instalada (kWp) — opcional
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={capacidadeKwp}
              onChange={e => setCapacidadeKwp(e.target.value)}
              placeholder="Ex: 75"
              aria-describedby={kwpTelemetria != null ? 'ug-capacidade-telemetria' : undefined}
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
            {kwpTelemetria != null && (
              <p id="ug-capacidade-telemetria" className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <span>
                  O inversor{nomeTelemetria ? ` (${nomeTelemetria})` : ''} informa{' '}
                  <strong className={divergente ? 'text-amber-700 dark:text-amber-400' : ''}>{kwpTelemetria.toLocaleString('pt-BR')} kWp</strong>.
                </span>
                {divergente && (
                  <button
                    type="button"
                    onClick={() => setCapacidadeKwp(String(kwpTelemetria))}
                    className="rounded border border-primary/40 px-1.5 py-0.5 font-medium text-primary hover:bg-primary/10"
                  >
                    Usar este valor
                  </button>
                )}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="ug-numero-longo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Número da UC na fatura (longo)
            </label>
            <input
              id="ug-numero-longo"
              type="text"
              inputMode="numeric"
              value={numeroLongo}
              onChange={e => setNumeroLongo(e.target.value)}
              placeholder="Ex: 4.060.193.012-38"
              aria-describedby="ug-numero-longo-aviso"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 font-mono"
            />
            <p id="ug-numero-longo-aviso" className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
              Precisa ser igual ao número da usina que aparece nas faturas. É por ele que a extração reconhece as usinas da Vigor.
            </p>
          </div>
          <div>
            <label htmlFor="ug-numero-antigo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Número na planilha de rateio (antigo)
            </label>
            <input
              id="ug-numero-antigo"
              type="text"
              inputMode="numeric"
              value={numeroAntigo}
              onChange={e => setNumeroAntigo(e.target.value)}
              placeholder="Ex: 10036788990"
              aria-describedby="ug-numero-antigo-ajuda"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 font-mono"
            />
            <p id="ug-numero-antigo-ajuda" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              O número de 11 dígitos da coluna da usina na planilha. É por ele que as UCs são ligadas à usina.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} />
            Ativa
          </label>
          {!ug && (
            <p className="text-xs text-gray-500 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
              Esta UG ficará <strong>pendente de aprovação</strong> de um Gestor até poder ser usada no Simulador.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>

      {showNovaDistribuidora && (
        <DistribuidoraFormModal
          onClose={() => setShowNovaDistribuidora(false)}
          onSaved={async novaDistribuidora => {
            setShowNovaDistribuidora(false);
            await carregarDistribuidoras();
            setDistribuidoraId(novaDistribuidora.id);
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default UGFormModal;
